/**
 * Line Charges Section — Apex Design System.
 *
 * The itemised charges on one order line, with an inline add form. Shared by
 * purchase orders and sales orders, which bill genuinely different things: a PO's
 * charges are costs, a sales order's are revenue the customer is billed for and
 * may or may not be taxable.
 *
 * That difference is carried in the type rather than in a runtime flag. The sales
 * side's `is_taxable` is required with no default on the server, precisely
 * because a charge that quietly defaults to non-taxable is the seller's liability
 * at audit — so `askTaxable` is a discriminated union, and the purchase caller
 * cannot pass the field at all. This matters more than it looks: Pydantic ignores
 * unknown fields by default, so an `is_taxable` sent to the purchase endpoint
 * would be silently dropped rather than rejected.
 *
 * There is deliberately no "apply a scheme to this line" affordance. Scheme
 * amounts are the server's to compute — per-unit multiples, per-order dedupe,
 * legacy rate fallbacks and taxability all live in the engine — and the
 * order-level "Apply Schemes" action already exposes it correctly.
 */

import { useEffect, useRef, useState } from "react";
import { DollarSign, Plus, Trash2 } from "lucide-react";

import Badge from "@/components/ui/Badge";
import {
  CHARGE_TYPES,
  chargeTypeLabel,
  DEFAULT_CHARGE_TYPE,
} from "@/components/ui/chargeTypes";

/** The shape of a charge, as much of it as this component needs. */
export interface LineChargeLike {
  id: string;
  charge_type: string;
  description: string;
  amount: number;
  /** Set when a scheme created the charge; null when entered by hand. */
  tax_scheme_id: string | null;
  /** Sales orders only — a purchase charge has no taxability. */
  is_taxable?: boolean;
}

/** Everything the section needs from a line of either order type. */
export interface ChargeableLine {
  id: string;
  charges?: LineChargeLike[];
}

interface NewCharge {
  charge_type: string;
  description: string;
  amount: number;
}

/**
 * Whether the add form asks for taxability, and the payload shape that follows.
 *
 * Discriminated so the two callers cannot accidentally share a handler.
 */
type ChargeAdd =
  | { askTaxable?: false; onAddCharge?: (data: NewCharge) => void }
  | {
      askTaxable: true;
      onAddCharge?: (data: NewCharge & { is_taxable: boolean }) => void;
    };

export type LineChargesSectionProps = ChargeAdd & {
  line: ChargeableLine;
  isEditable: boolean;
  onDeleteCharge?: (chargeId: string) => void;
  isAddingCharge?: boolean;
  /**
   * Purchase orders only: the legacy `line_fees` scalar, which predates itemised
   * charges and is displayed as a pseudo-row so it can be cleared. Sales orders
   * omit it — there `line_fees` is a derived sum of the charges below, not an
   * independent value, so showing it here would double-count.
   */
  legacyFee?: {
    amount: number;
    description: string | null;
    onClear: () => void;
  };
};

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

const inputClass =
  "mt-1 block w-full h-[34px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2.5 text-xs text-[var(--text-primary)]";
const labelClass =
  "block text-[10px] font-medium uppercase tracking-wider text-[var(--text-tertiary)]";

export default function LineChargesSection(props: LineChargesSectionProps) {
  const {
    line,
    isEditable,
    onDeleteCharge,
    isAddingCharge,
    legacyFee,
    onAddCharge,
  } = props;
  const askTaxable = props.askTaxable === true;

  const [showAddRow, setShowAddRow] = useState(false);
  const [addType, setAddType] = useState(DEFAULT_CHARGE_TYPE);
  const [addDesc, setAddDesc] = useState("");
  const [addAmount, setAddAmount] = useState("");
  // Undecided until the user says. Never preselected: the whole point of the
  // server requiring it is that somebody makes the call consciously.
  const [addTaxable, setAddTaxable] = useState<boolean | null>(null);
  const descRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showAddRow) descRef.current?.focus();
  }, [showAddRow]);

  const charges = line.charges ?? [];
  const legacyAmount = legacyFee?.amount ?? 0;
  const chargesTotal = charges.reduce((sum, c) => sum + Number(c.amount), 0);
  const feesTotal = legacyAmount + chargesTotal;

  const amountValue = parseFloat(addAmount);
  const canAdd =
    !!addDesc.trim() &&
    !isNaN(amountValue) &&
    amountValue > 0 &&
    (!askTaxable || addTaxable !== null) &&
    !isAddingCharge;

  const resetForm = () => {
    setShowAddRow(false);
    setAddDesc("");
    setAddAmount("");
    setAddType(DEFAULT_CHARGE_TYPE);
    setAddTaxable(null);
  };

  const handleAdd = () => {
    if (!canAdd || !onAddCharge) return;
    const base = {
      charge_type: addType,
      description: addDesc.trim(),
      amount: amountValue,
    };
    if (askTaxable) {
      (onAddCharge as (d: NewCharge & { is_taxable: boolean }) => void)({
        ...base,
        is_taxable: addTaxable === true,
      });
    } else {
      (onAddCharge as (d: NewCharge) => void)(base);
    }
    resetForm();
  };

  const onFormKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleAdd();
    if (e.key === "Escape") resetForm();
  };

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />
          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
            Fees &amp; Charges
          </h4>
        </div>
        {isEditable && onAddCharge && !showAddRow && (
          <button
            type="button"
            onClick={() => setShowAddRow(true)}
            className="flex h-6 items-center gap-1 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-2 text-[10px] font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
          >
            <Plus className="h-3 w-3" />
            Add
          </button>
        )}
      </div>

      {/* Charge list */}
      <div className="space-y-0">
        {legacyFee && legacyAmount > 0 && (
          <div className="flex items-center gap-2 border-b border-[var(--border-subtle)]/50 py-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <Badge variant="default">fee</Badge>
                <span className="truncate text-xs text-[var(--text-primary)]">
                  {legacyFee.description || "Line fee"}
                </span>
              </div>
            </div>
            <span className="shrink-0 text-xs font-medium tabular-nums text-[var(--text-primary)]">
              {fmt(legacyAmount)}
            </span>
            {isEditable && (
              <button
                type="button"
                onClick={legacyFee.onClear}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--text-tertiary)] transition-colors hover:bg-[var(--fill-danger)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        )}

        {charges.map((c) => (
          <div
            key={c.id}
            className="flex items-center gap-2 border-b border-[var(--border-subtle)]/50 py-2 last:border-0"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <Badge variant={c.tax_scheme_id ? "info" : "default"}>
                  {c.tax_scheme_id ? "scheme" : chargeTypeLabel(c.charge_type)}
                </Badge>
                {/* Taxability is what the whole fee/tax design turns on. If it is
                    not visible, nobody can audit it. */}
                {c.is_taxable && <Badge variant="warning">taxable</Badge>}
                <span className="truncate text-xs text-[var(--text-primary)]">
                  {c.description}
                </span>
              </div>
            </div>
            <span className="shrink-0 text-xs font-medium tabular-nums text-[var(--text-primary)]">
              {fmt(Number(c.amount))}
            </span>
            {isEditable && onDeleteCharge && (
              <button
                type="button"
                onClick={() => onDeleteCharge(c.id)}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-[var(--text-tertiary)] transition-colors hover:bg-[var(--fill-danger)] hover:text-[var(--text-danger)]"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}

        {charges.length === 0 && legacyAmount === 0 && !showAddRow && (
          <p className="py-2 text-xs text-[var(--text-tertiary)]">
            No fees or charges applied.
          </p>
        )}
      </div>

      {/* Inline add form */}
      {isEditable && showAddRow && onAddCharge && (
        <div className="mt-2 space-y-2.5 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)]/30 p-3">
          <div>
            <label className={labelClass}>Type</label>
            <select
              value={addType}
              onChange={(e) => setAddType(e.target.value)}
              className={`${inputClass} cursor-pointer`}
            >
              {CHARGE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <input
              ref={descRef}
              type="text"
              value={addDesc}
              onChange={(e) => setAddDesc(e.target.value)}
              onKeyDown={onFormKey}
              placeholder="Charge description"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              onKeyDown={onFormKey}
              placeholder="0.00"
              className={`${inputClass} [appearance:textfield] text-right tabular-nums [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`}
            />
          </div>

          {askTaxable && (
            <div>
              <label className={labelClass}>Taxable</label>
              <div className="mt-1 flex gap-2">
                {[
                  { value: true, label: "Taxable" },
                  { value: false, label: "Not taxable" },
                ].map((opt) => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => setAddTaxable(opt.value)}
                    className={`h-[30px] flex-1 rounded-[var(--radius-md)] border text-[11px] font-medium transition-colors ${
                      addTaxable === opt.value
                        ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
                        : "border-[var(--border-default)] bg-[var(--surface-base)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-[var(--text-tertiary)]">
                Whether this charge is itself taxed. It varies by state and by
                whether the charge is separately stated, so there is no default.
              </p>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              className="flex h-7 items-center gap-1.5 rounded-[var(--radius-md)] bg-[var(--color-interactive-primary)] px-3 text-[11px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              {isAddingCharge ? (
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              Add Charge
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="text-[11px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Subtotal */}
      {feesTotal > 0 && (
        <div className="mt-3 flex items-center justify-between border-t border-[var(--border-default)] pt-2.5">
          <span className="text-[11px] font-medium text-[var(--text-secondary)]">
            Fees &amp; Charges Subtotal
          </span>
          <span className="text-sm font-semibold tabular-nums text-[var(--text-primary)]">
            {fmt(feesTotal)}
          </span>
        </div>
      )}
    </>
  );
}
